import { Button, Checkbox, Col, Form, message, Row, Typography } from "antd";
import { ReactComponent as ChevronLeft } from "../../assets/chevron-left.svg";
import { ReactComponent as IconClinical } from "../../assets/free_clinical_tool.svg";
import { ReactComponent as IconEducation } from "../../assets/educational_content.svg";
import { ReactComponent as IconAdvisory } from "../../assets/advisory_survey.svg";
import { ReactComponent as IconNetwork } from "../../assets/professional_networking.svg";
import { ReactComponent as IconEarlyAccess } from "../../assets/early_access.svg";
import { ReactComponent as IconCareer } from "../../assets/career_building.svg";
import signupHero from "../../assets/signup-hero.png";
import signupHeroMobile from "../../assets/signup-hero-mobile.png";
import { useForm } from "../../utils/hooks/useForm";
import FloatInput from "../../components/FloatInput/floatInput";
import "./signup.modules.css";
import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../utils/hooks/useAuth";
import {
  allowNumOnly,
  confirmPasswordValidation,
  emailPattenValidation,
  passwordPatternValidation,
  policyCheckboxValidation,
  usPhoneNumberRule,
  validateExactLength,
  validateMaxValue,
  validateNPIPattern,
} from "../../utils/methods/form-validations";
import { NamePath, StoreValue } from "antd/es/form/interface";
import WPLayout from "../../components/WPLayout/WPLayout";
import NetworkOtpStep from "./NetworkOtpStep";

/* Natural order = the mobile comp's top-to-bottom order. On md+ the
   grid flows column-major (rows of 3), reproducing the desktop columns:
   left = clinical / education / advisory, right = network / early / career */
const MEMBER_BENEFITS = [
  { icon: <IconClinical />, text: "Free clinical tools and treatment resources" },
  {
    icon: <IconEducation />,
    text: "Educational content and physician-led insights",
  },
  {
    icon: <IconAdvisory />,
    text: "Advisory, survey, and compensated engagement opportunities",
  },
  {
    icon: <IconNetwork />,
    text: "Professional networking, mentorship and community experiences",
  },
  {
    icon: <IconEarlyAccess />,
    text: "Early access to new programs, products, and initiatives",
  },
  {
    icon: <IconCareer />,
    text: "Career-building opportunities across healthcare and industry",
  },
];

const SignupV1 = () => {
  const { form } = useForm();
  const { findUserByNPI, isApiLoading, smsSignUp } = useAuth(form);
  const [tabKey, setTabKey] = React.useState(1);
  const [showPlaceholder, setShowPlaceholder] = React.useState<boolean>(true);
  const [isPhoneHasError, setIsPhoneHasError] = React.useState<boolean>(false);
  const [otpState, setOtpState] = React.useState<{
    phoneNumber: string;
    npiNumber: number;
  } | null>(null);

  const formatPhoneNumber = (value: string): string => {
    const digits = value.replace(/\D/g, "").slice(0, 10);
    if (!digits) return "";
    const area = digits.slice(0, 3);
    const prefix = digits.slice(3, 6);
    const line = digits.slice(6, 10);
    if (digits.length <= 3) return "(" + area;
    if (digits.length <= 6) return "(" + area + ") " + prefix;
    return "(" + area + ") " + prefix + "-" + line;
  };

  const handleFinishNetwork = (values: StoreValue) => {
    if (tabKey === 1) {
      findUserByNPI({ ...values }, setTabKey, form);
    } else if (tabKey === 2) {
      // Stay on this page: on success the OTP step replaces the form here
      smsSignUp({ ...values }, setShowPlaceholder, form, setOtpState);
    }
  };

  const onNetworkFinishFailed = (errorInfo: StoreValue) => {
    const checkPhoneNumber =
      form.getFieldError("network_phoneNumber")?.length > 0;
    setIsPhoneHasError(checkPhoneNumber);
    if (
      errorInfo.values.privacyPolicyAgreement &&
      errorInfo.errorFields.length &&
      tabKey === 2
    ) {
      message.error(
        "Please complete all required fields in the previous steps before proceeding.",
      );
    }
  };

  return (
    <WPLayout>
      <div className="suv2 w-full flex flex-col items-center">
        {/* ===== Aqua surface: hero + intro + sign-up card ===== */}
        <div className="suv2-surface w-full flex flex-col items-center">
          {/* Hero (no dot texture) */}
          <div className="w-full max-w-[1120px] flex flex-col items-center px-5 sm:px-8">
            <Typography.Text className="suv2-h1 block font-playfair font-semibold text-center">
              Join the DocUpdate Community
            </Typography.Text>
            <picture className="w-full">
              <source media="(min-width: 1024px)" srcSet={signupHero} />
              <img
                src={signupHeroMobile}
                alt="Physicians connecting at a DocUpdate community event"
                className="suv2-hero-img w-full h-auto"
              />
            </picture>
          </div>

          {/* Dot texture starts below the hero image */}
          <div className="suv2-dotted w-full flex flex-col items-center px-5 sm:px-8">
            {/* Intro copy + Sign-up card */}
            <div className="suv2-main-grid w-full max-w-[1120px]">
            <div className="flex flex-col gap-y-7">
              <Typography.Paragraph className="suv2-lead">
                Join the largest physician-founded network and platform
                connecting clinicians to the tools, resources, professional
                opportunities, and relationships you need to accelerate access
                to care.
              </Typography.Paragraph>
              <Typography.Paragraph className="suv2-lead">
                More than an app, DocUpdate is a community built to support
                your professional growth, expand your impact, and connect you
                with the resources and opportunities that help you thrive in
                healthcare.
              </Typography.Paragraph>
            </div>

            <div className="w-full max-w-[500px] mx-auto lg:mx-0">
              {/* The card wraps both the signup form and the in-place OTP
                  step. The form stays mounted (hidden) during OTP so its
                  values survive a "back" to editing. */}
              <div className="suv2-card w-full flex flex-col bg-white">
              <Form
                form={form}
                name="network-signup"
                onFinish={handleFinishNetwork}
                onFinishFailed={onNetworkFinishFailed}
                className="w-full flex flex-col"
                style={{ display: otpState ? "none" : undefined }}
              >
                {/* Card header: back / title */}
                <div className="relative flex items-center justify-center mb-7">
                  {tabKey === 2 && (
                    <Button
                      className="suv2-step-btn absolute left-0"
                      icon={<ChevronLeft />}
                      onClick={() => setTabKey(1)}
                      aria-label="Back to NPI and phone number"
                    />
                  )}
                  <Typography.Text className="suv2-card-title block font-playfair font-bold text-center">
                    Sign Up
                  </Typography.Text>
                </div>

                {/* Tab container */}
                <div className="w-full">
                  {/* Tab 1 fields stay mounted (display:none when inactive) so
                      their values stay in the form store and reach the API on
                      tab-2 submit. Unmounting Form.Items strips their values. */}
                  <div
                    className={
                      tabKey === 1 ? "w-full animate-fade-in" : "w-full"
                    }
                    style={{ display: tabKey === 1 ? "block" : "none" }}
                  >
                    <Row
                      gutter={[12, 20]}
                      wrap
                      align={"middle"}
                      justify={"center"}
                    >
                      <Col span={24}>
                        <Form.Item
                          name="network_npiNumber"
                          rules={[
                            {
                              required: true,
                              message: "*NPI is Required",
                            },
                            validateNPIPattern(),
                            validateExactLength("NPI", 10),
                            validateMaxValue("NPI", 2147483647),
                          ]}
                          validateFirst
                          preserve
                        >
                          <FloatInput
                            name={"network_npiNumber"}
                            type="text"
                            placeholder={"1234567890"}
                            maxLength={10}
                            label={"NPI"}
                            required={true}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>,
                            ) => allowNumOnly(e, form, "network_npiNumber")}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={24} className="relative md:mb-1">
                        <Form.Item
                          name="network_phoneNumber"
                          rules={[usPhoneNumberRule]}
                          validateFirst
                          preserve
                        >
                          <FloatInput
                            label="Mobile Phone Number"
                            type="text"
                            required={true}
                            placeholder="(555) 555-5555"
                            value={form.getFieldValue("network_phoneNumber")}
                            onChange={(e: any) =>
                              form.setFieldsValue({
                                network_phoneNumber: formatPhoneNumber(
                                  e.target.value,
                                ),
                              })
                            }
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </div>

                  <div
                    className={
                      tabKey === 2 ? "w-full animate-fade-in" : "w-full"
                    }
                    style={{ display: tabKey === 2 ? "block" : "none" }}
                  >
                    <Row gutter={8}>
                      <Col span={24} className="p-0 mb-3">
                        <Form.Item
                          name={"network_firstName"}
                          rules={[
                            {
                              required: !!(tabKey === 2),
                              message: "*First Name is Required",
                            },
                          ]}
                        >
                          <FloatInput
                            name={"network_firstName"}
                            placeholder={"Sam"}
                            label="First Name"
                            required={true}
                            type="text"
                          />
                        </Form.Item>
                      </Col>
                      <Col span={24} className="p-0 mb-3">
                        <Form.Item
                          name={"network_lastName"}
                          rules={[
                            {
                              required: !!(tabKey === 2),
                              message: "*Last Name is Required",
                            },
                          ]}
                        >
                          <FloatInput
                            name={"network_lastName"}
                            type="text"
                            label="Last Name"
                            placeholder={"Smith"}
                            required
                          />
                        </Form.Item>
                      </Col>
                      <Col span={24} className="mb-4">
                        <Form.Item
                          name="network_email"
                          rules={[
                            {
                              required: !!(tabKey === 2),
                              message: "*Email Address is Required",
                            },
                            ...(tabKey === 2 ? [emailPattenValidation()] : []),
                          ]}
                          validateFirst
                        >
                          <FloatInput
                            name={"network_email"}
                            type="text"
                            placeholder={"name@email.com"}
                            label="Email Address"
                            required={true}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={24} className="mb-6">
                        <Form.Item
                          name="network_password"
                          rules={[
                            {
                              required: !!(tabKey === 2),
                              message: "*Password is Required",
                            },
                            ...(tabKey === 2
                              ? [passwordPatternValidation()]
                              : []),
                          ]}
                          validateFirst
                          hasFeedback
                        >
                          <FloatInput
                            name={"network_password"}
                            type="password"
                            placeholder={"********"}
                            required={true}
                            label="Password"
                          />
                        </Form.Item>
                      </Col>
                      <Col span={24} className="mb-2">
                        <Form.Item
                          name="confirmPassword"
                          rules={[
                            {
                              required: !!(tabKey === 2),
                              message: "*Confirm Password is Required",
                            },
                            ...(tabKey === 2
                              ? [
                                  ({
                                    getFieldValue,
                                  }: {
                                    getFieldValue: (
                                      name: NamePath,
                                    ) => StoreValue;
                                  }) =>
                                    confirmPasswordValidation(getFieldValue),
                                ]
                              : []),
                          ]}
                          validateFirst
                          hasFeedback
                        >
                          <FloatInput
                            name={"confirmPassword"}
                            type="password"
                            placeholder={"********"}
                            required={true}
                            label="Confirm Password"
                          />
                        </Form.Item>
                      </Col>
                      <div className="mt-4 flex justify-center items-center mx-auto">
                        <Form.Item
                          name={"privacyPolicyAgreement"}
                          valuePropName="checked"
                          rules={[policyCheckboxValidation(tabKey)]}
                          className="consent-form"
                        >
                          <Checkbox className="custom-checkbox w-full max-w-[440px] font-urbanist leading-none text-xs text-[#050505]">
                            By submitting this form and signing up for texts,
                            you consent to receive messages from DocUpdate at
                            the number provided, including messages sent by an
                            autodialer or AI generated messages or voice calls.
                            Consent is not a condition of purchase. Msg & data
                            rates may apply. Msg frequency varies. Unsubscribe
                            at any time by replying STOP or clicking the
                            unsubscribe link (where available). I read and agree
                            with the DocUpdate{" "}
                            <Link
                              to={"https://www.docupdate.io/ccpa-notice/"}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[#7056D6] underline font-lato font-bold"
                            >
                              {" "}
                              CCPA Policy
                            </Link>
                            {", "}
                            <Link
                              to={"https://www.docupdate.io/privacy-policy"}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[#7056D6] underline font-lato font-bold"
                            >
                              Privacy Policy
                            </Link>{" "}
                            and{" "}
                            <Link
                              to={"https://www.docupdate.io/terms-of-service/"}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[#7056D6] underline font-lato font-bold"
                            >
                              Terms of Service
                            </Link>
                            .
                          </Checkbox>
                        </Form.Item>
                      </div>
                    </Row>
                  </div>
                </div>

                <Col
                  span={24}
                  className="mt-3 flex justify-center items-center w-full"
                >
                  <Button
                    htmlType="submit"
                    className="suv2-cta w-full border-none font-semibold disabled:opacity-45"
                    loading={isApiLoading}
                    iconPosition="end"
                  >
                    Join the DocUpdate Community
                  </Button>
                </Col>
              </Form>

              {otpState && (
                <NetworkOtpStep
                  phoneNumber={otpState.phoneNumber}
                  npiNumber={otpState.npiNumber}
                  onBack={() => setOtpState(null)}
                />
              )}
              </div>
            </div>
            </div>
          </div>
        </div>

        {/* ===== Membership benefits band ===== */}
        <section className="suv2-band w-full px-5 sm:px-8">
          <div className="mx-auto w-full max-w-[1180px] flex flex-col items-center">
            <Typography.Text className="suv2-band-title block font-playfair font-semibold text-center !text-white">
              As a member, you&rsquo;ll gain access to:
            </Typography.Text>
            <div className="suv2-benefits-grid w-full">
              {MEMBER_BENEFITS.map((benefit) => (
                <div key={benefit.text} className="suv2-benefit">
                  <span className="suv2-benefit-icon">{benefit.icon}</span>
                  <Typography.Text className="suv2-benefit-text !text-white">
                    {benefit.text}
                  </Typography.Text>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </WPLayout>
  );
};

export default SignupV1;